import { apiSlice } from './apiSlice'

/**
 * Sale Allocation — "Print - VGM" chooser + report (Access `frmCtrlSalesVGMPr_Choose`
 * + `rpCtrlSalesVGM_NN` / `rpCtrlSalesVGMTotal`).
 *
 * Access port (VBA `btnPrintVGM_Click` on `frmCtrlSalesMainSubFormSaleAlc`):
 *   1. DELETE FROM public_tmp_sales_alc_tick
 *   2. INSERT INTO public_tmp_sales_alc_tick
 *        SELECT *, 1 AS checked_row FROM public_v_sales_allocation_scs
 *        WHERE delivery_order = '<do>' AND IIf(IsNull(container_no),'',container_no) <> ''
 *   3. Open chooser `frmCtrlSalesVGMPr_Choose` bound to that DO.
 * Chooser lets the user tick the containers to print + pick a container size, then
 * `btnPrint_Click` builds `CtrlSalesQryVGMPrint` over the ticked container_no list and
 * opens `rpCtrlSalesVGM_<count>` (1..15 rows) or `rpCtrlSalesVGMTotal` for larger sets.
 *
 * NOTE — BACKEND NOT YET BUILT. Every endpoint below is a placeholder mirroring the
 * proven `saleDeliveryOrderApi` shape so the chooser dialog can compile and wire today.
 * The URLs marked `TODO BE` MUST be implemented before this dialog is enabled in
 * production. Queries fall back to empty payloads (no silent fake data) until then.
 *
 * Cache tag reused: `SaleControl` (shared with the Sales Allocation datasheet).
 */

/** One selectable container row for the VGM chooser tick list.
 *  Sourced from `public_v_sales_allocation_scs` for the given delivery_order
 *  (only rows with a non-empty container_no). */
export interface VgmContainerRow {
  /** Stable key — the allocation row PK. */
  salesAllocationScsId: number
  containerNo: string
  /** Max gross weight (kg) — report column C. */
  maxGrossWeight: number | null
  /** Verified gross mass (kg) — report column D. */
  vgm: number | null
}

export interface VgmContainersResponse {
  deliveryOrder: string
  items: VgmContainerRow[]
}

/** Payload for the VGM print preview — built from the ticked containers + size. */
export interface VgmPrintPreviewRequest {
  /** Selected delivery_order (chooser is bound to one DO). */
  doNo: string
  /** Ticked container_no list (report rows, max 15 for `rpCtrlSalesVGM_NN`). */
  containerNos: string[]
  /** `container_type` combo — Access Value List `"20 '";"40 '";"other"`. */
  containerType: string
}

export interface VgmPrintPreviewResponse {
  /** Fully-built HTML (`access-report` + `.ctl` absolute layout) for the iframe. */
  html: string
  /** Suggested dialog title id part (the delivery_order). */
  title: string
  /** Optional info/warning relayed to the user (English). */
  message?: string
}

const saleAllocationVgmPrintApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Containers eligible for the VGM chooser tick list.
     * Access: DELETE/INSERT into public_tmp_sales_alc_tick then bind the subform.
     * TODO BE — GET /sale-allocations/vgm-print/containers?do_no=<do>
     *   → { deliveryOrder, items: VgmContainerRow[] } from public_v_sales_allocation_scs
     *     WHERE delivery_order = :do AND container_no <> ''.
     */
    getVgmPrintContainers: builder.query<VgmContainersResponse, string>({
      query: (doNo) => ({
        url: '/sale-allocations/vgm-print/containers',
        params: { do_no: doNo },
      }),
      providesTags: ['SaleControl'],
    }),

    /**
     * Build the VGM report HTML for the ticked containers.
     * Access: `btnPrint_Click` builds `CtrlSalesQryVGMPrint` + opens rpCtrlSalesVGM_NN.
     * TODO BE — POST /sale-allocations/vgm-print/preview
     *   body VgmPrintPreviewRequest → { html, title, message? }.
     *   BE picks the row count → renders the matching `rpCtrlSalesVGM_NN` template.
     */
    getVgmPrintPreview: builder.query<VgmPrintPreviewResponse, VgmPrintPreviewRequest>({
      query: (body) => ({
        url: '/sale-allocations/vgm-print/preview',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetVgmPrintContainersQuery,
  useLazyGetVgmPrintPreviewQuery,
} = saleAllocationVgmPrintApi

export default saleAllocationVgmPrintApi
